// EtudiantClient.jsx - Version avec Tailwind CSS natif
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import {
    Users, Plus, Search, Mail, Trash2, Edit,
    Upload, Download, Filter, ChevronDown, ChevronRight,
    UserPlus, Bell, AlertCircle, X, CheckCircle,
    Loader2, RefreshCw, Building2,
    Shield, ShieldCheck, ShieldAlert, ShieldOff,
    UserCheck, UserX, Hash, BookOpen, Key, Send,
    Power, PowerOff
} from 'lucide-react';

// ============================================================
// 1. COMPOSANT MODAL DE NOTIFICATION
// ============================================================
const NotificationModal = ({ isOpen, onClose, onSend, students, loading, defaultStudentId = null }) => {
    const [formData, setFormData] = useState({
        type: 'EMAIL',
        message: '',
        subject: '',
        sendToAll: false,
        selectedStudents: []
    });
    const [searchQuery, setSearchQuery] = useState('');
    const hasPreselectedRef = useRef(false);
    const isClosingRef = useRef(false);

    useEffect(() => {
        if (isOpen && defaultStudentId && !hasPreselectedRef.current && !isClosingRef.current) {
            hasPreselectedRef.current = true;
            const timer = setTimeout(() => {
                setFormData(prev => ({
                    ...prev,
                    selectedStudents: prev.selectedStudents.includes(defaultStudentId)
                        ? prev.selectedStudents
                        : [...prev.selectedStudents, defaultStudentId]
                }));
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [isOpen, defaultStudentId]);

    useEffect(() => {
        if (!isOpen) {
            isClosingRef.current = true;
            hasPreselectedRef.current = false;
            const timer = setTimeout(() => {
                setFormData({
                    type: 'EMAIL',
                    message: '',
                    subject: '',
                    sendToAll: false,
                    selectedStudents: []
                });
                setSearchQuery('');
                isClosingRef.current = false;
            }, 0);
            return () => {
                clearTimeout(timer);
                isClosingRef.current = false;
            };
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const addStudent = (id) => {
        if (!formData.selectedStudents.includes(id)) {
            setFormData(prev => ({
                ...prev,
                selectedStudents: [...prev.selectedStudents, id]
            }));
        }
        setSearchQuery('');
    };

    const removeStudent = (id) => {
        setFormData(prev => ({
            ...prev,
            selectedStudents: prev.selectedStudents.filter(sid => sid !== id)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const recipients = formData.sendToAll
            ? students.map(s => s.id)
            : formData.selectedStudents;

        if (recipients.length === 0) {
            alert('Veuillez sélectionner au moins un étudiant');
            return;
        }

        await onSend({
            ...formData,
            studentIds: recipients
        });
    };

    const filteredStudents = students.filter(s =>
        !formData.selectedStudents.includes(s.id) &&
        (s.prenom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         s.nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         s.email?.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                        <Bell size={20} className="text-green-600" />
                        Envoyer une notification
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select
                            value={formData.type}
                            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                            <option value="EMAIL">📧 Email</option>
                            <option value="SMS">📱 SMS</option>
                            <option value="PUSH">🔔 Push</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Objet</label>
                        <input
                            type="text"
                            value={formData.subject}
                            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                        <textarea
                            value={formData.message}
                            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            required
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.sendToAll}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                sendToAll: e.target.checked,
                                selectedStudents: e.target.checked ? [] : prev.selectedStudents
                            }))}
                            className="w-4 h-4 text-green-600 rounded"
                        />
                        <label className="text-sm text-gray-700">Envoyer à tous les étudiants</label>
                    </div>
                    {!formData.sendToAll && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Destinataires</label>
                            <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-lg min-h-[50px] bg-gray-50">
                                {formData.selectedStudents.length === 0 ? (
                                    <span className="text-sm text-gray-400">Aucun destinataire</span>
                                ) : (
                                    formData.selectedStudents.map(id => {
                                        const s = students.find(st => st.id === id);
                                        return s ? (
                                            <span key={id} className="inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm rounded-full">
                                                {s.prenom} {s.nom} ({s.email})
                                                <button type="button" onClick={() => removeStudent(id)} className="ml-1 hover:text-gray-200">&times;</button>
                                            </span>
                                        ) : null;
                                    })
                                )}
                            </div>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    placeholder="Rechercher un étudiant..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                />
                                {searchQuery && filteredStudents.length > 0 && (
                                    <div className="mt-1 max-h-32 overflow-y-auto border border-gray-200 rounded-lg">
                                        {filteredStudents.map(s => (
                                            <div
                                                key={s.id}
                                                onClick={() => addStudent(s.id)}
                                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-0"
                                            >
                                                {s.prenom} {s.nom} ({s.email})
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {searchQuery && filteredStudents.length === 0 && (
                                    <div className="mt-1 text-sm text-gray-400">Aucun étudiant trouvé</div>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">
                            Annuler
                        </button>
                        <button type="submit" disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm flex items-center gap-2">
                            {loading ? 'Envoi...' : '📤 Envoyer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ============================================================
// 2. MODAL RÉINITIALISATION MOT DE PASSE
// ============================================================
const ResetPasswordModal = ({ isOpen, onClose, onReset, student, loading }) => {
    if (!isOpen || !student) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        await onReset(student.id, null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                        <Key size={20} className="text-green-600" />
                        Réinitialiser le mot de passe
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="bg-blue-50 p-3 rounded-lg text-sm">
                        <p className="text-gray-700"><strong>Étudiant :</strong> {student.prenom} {student.nom}</p>
                        <p className="text-gray-500 text-xs"><strong>Email :</strong> {student.email}</p>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 text-xs text-yellow-700">
                        ⚠️ Un nouveau mot de passe sera généré automatiquement et envoyé par email.
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">
                            Annuler
                        </button>
                        <button type="submit" disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">
                            {loading ? 'Réinitialisation...' : '🔑 Réinitialiser'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ============================================================
// 3. MODAL IMPORT CSV
// ============================================================
const ImportModal = ({ isOpen, onClose, onImport, loading }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState([]);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        setFile(selected);
        if (selected) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                const lines = text.split('\n').filter(line => line.trim());
                const headers = lines[0].split(',').map(h => h.trim());
                const data = lines.slice(1, 6).map(line => {
                    const values = line.split(',').map(v => v.trim());
                    return headers.reduce((obj, h, i) => ({ ...obj, [h]: values[i] || '' }), {});
                });
                setPreview(data);
            };
            reader.readAsText(selected);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                        <Upload size={20} className="text-green-600" />
                        Importer des étudiants (CSV)
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); onImport(file); }} className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition">
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700 cursor-pointer"
                        />
                        <p className="text-xs text-gray-400 mt-2">Format: nom, prenom, email, telephone, dateNaissance</p>
                    </div>
                    {preview.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-600 mb-2">Aperçu des données</p>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {Object.keys(preview[0] || {}).map(key => (
                                                <th key={key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{key}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {preview.map((row, idx) => (
                                            <tr key={idx}>
                                                {Object.values(row).map((val, i) => (
                                                    <td key={i} className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{val}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">
                            Annuler
                        </button>
                        <button type="submit" disabled={loading || !file} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">
                            {loading ? 'Import...' : '📥 Importer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ============================================================
// 4. COMPOSANT PRINCIPAL
// ============================================================
export default function EtudiantClient() {
    const { token, user, isAuthenticated } = useAuth();
    const clientId = user?.clientId || user?.client?.id || user?.id;

    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');

    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalPages: 0,
        totalElements: 0
    });

    const [showFormModal, setShowFormModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [deletingStudent, setDeletingStudent] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedInscription, setSelectedInscription] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
    const [notificationStudentId, setNotificationStudentId] = useState(null);

    // Nouvel état pour le mode force
    const [forceDelete, setForceDelete] = useState(false);

    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        dateNaissance: '',
        adresse: '',
        statut: 'ACTIF'
    });

    const [notificationLoading, setNotificationLoading] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    // ============================================================
    // 5. RÉCUPÉRATION DES ÉTUDIANTS
    // ============================================================
    const fetchStudents = useCallback(async (page = 0) => {
        if (!token || !clientId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let url = `${APP_CONFIG.BASE_URL}/api/v1/client/etudiants?page=${page}&size=${pagination.size}`;

            if (searchTerm) {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }
            if (filterStatus !== 'ALL') {
                url += `&statut=${encodeURIComponent(filterStatus)}`;
            }

            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                if (response.status === 401) throw new Error('Session expirée');
                if (response.status === 403) throw new Error('Accès refusé');
                throw new Error(`Erreur HTTP ${response.status}`);
            }

            const responseData = await response.json();
            const studentsData = responseData.content || responseData.data || responseData || [];
            setStudents(Array.isArray(studentsData) ? studentsData : []);

            // Récupérer les inscriptions pour chaque étudiant
            if (studentsData.length > 0) {
                for (const student of studentsData) {
                    try {
                        const inscriptionsRes = await fetch(
                            `${APP_CONFIG.BASE_URL}/api/v1/client/etudiants/${student.id}/inscriptions-details`,
                            {
                                headers: {
                                    "Authorization": `Bearer ${token}`,
                                    "Accept": "application/json"
                                }
                            }
                        );
                        if (inscriptionsRes.ok) {
                            const inscriptionsData = await inscriptionsRes.json();
                            const inscriptions = inscriptionsData.data || inscriptionsData || [];
                            if (inscriptions.length > 0) {
                                student.inscription = inscriptions[0];
                            }
                        }
                    } catch (err) {
                        console.warn(`Impossible de récupérer les inscriptions pour l'étudiant ${student.id}`, err);
                    }
                }
                setStudents([...studentsData]);
            }

            setPagination({
                page: responseData.page || responseData.number || 0,
                size: responseData.size || responseData.pageSize || 10,
                totalPages: responseData.totalPages || responseData.total_pages || 0,
                totalElements: responseData.totalElements || responseData.total_elements || studentsData.length
            });
        } catch (err) {
            console.error("Erreur récupération étudiants:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, clientId, pagination.size, searchTerm, filterStatus]);

    useEffect(() => {
        fetchStudents();
    }, [fetchStudents]);

    // ============================================================
    // 6. RÉCUPÉRER LES INSCRIPTIONS D'UN ÉTUDIANT
    // ============================================================
    const fetchStudentInscriptions = useCallback(async (studentId) => {
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/client/etudiants/${studentId}/inscriptions-details`,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Accept": "application/json"
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                return data.data || data || [];
            }
            return [];
        } catch (err) {
            console.error("Erreur récupération inscriptions:", err);
            return [];
        }
    }, [token]);

    // ============================================================
    // 7. RÉINITIALISER LE MOT DE PASSE
    // ============================================================
    const handleResetPassword = useCallback(async (studentId, newPassword) => {
        setResetPasswordLoading(true);
        setError(null);

        try {
            const body = newPassword ? { newPassword } : {};
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/client/etudiants/${studentId}/reset-password`,
                {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    },
                    body: JSON.stringify(body)
                }
            );

            if (response.ok) {
                setShowResetPasswordModal(false);
                setSelectedStudent(null);
                alert(`✅ Mot de passe réinitialisé avec succès. Un email a été envoyé à l'étudiant.`);
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Erreur lors de la réinitialisation");
                alert(`❌ ${errorData.message || "Erreur lors de la réinitialisation"}`);
            }
        } catch (err) {
            console.error("Erreur réinitialisation:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setResetPasswordLoading(false);
        }
    }, [token]);

    // ============================================================
    // 8. ACTIVER/DÉSACTIVER UN ÉTUDIANT
    // ============================================================
    const handleToggleActivation = useCallback(async (student) => {
        const newStatus = !student.actif;
        const action = newStatus ? 'activer' : 'désactiver';

        if (!window.confirm(`Êtes-vous sûr de vouloir ${action} l'étudiant ${student.prenom} ${student.nom} ?`)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/client/etudiants/${student.id}/status?actif=${newStatus}`,
                {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    }
                }
            );

            if (response.ok) {
                await fetchStudents(pagination.page);

                if (!newStatus) {
                    await handleSendNotification({
                        type: 'EMAIL',
                        subject: 'Compte désactivé',
                        message: `Bonjour ${student.prenom} ${student.nom},\n\nVotre compte a été désactivé. Pour toute question, veuillez contacter l'administrateur.\n\nCordialement,\nL'équipe de formation.`,
                        studentIds: [student.id]
                    });
                }

                alert(`✅ Étudiant ${action} avec succès`);
            } else {
                const errorData = await response.json();
                setError(errorData.message || `Erreur lors de l'${action}`);
                alert(`❌ ${errorData.message || `Erreur lors de l'${action}`}`);
            }
        } catch (err) {
            console.error("Erreur activation/désactivation:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    }, [token, fetchStudents, pagination.page]);

    // ============================================================
    // 9. CHANGER LE STATUT DE L'ÉTUDIANT (ACTIF, INACTIF, SUSPENDU)
    // ============================================================
    const handleChangeStatut = useCallback(async (inscriptionId, nouveauStatut) => {
        if (!window.confirm(`Changer le statut de l'étudiant vers "${nouveauStatut}" ?`)) return;

        setStatusLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/client/inscriptions/${inscriptionId}/statut-etudiant?statut=${nouveauStatut}`,
                {
                    method: "PATCH",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    }
                }
            );

            if (response.ok) {
                setShowStatusModal(false);
                setSelectedStudent(null);
                setSelectedInscription(null);
                await fetchStudents(pagination.page);
                alert(`✅ Statut de l'étudiant mis à jour vers "${nouveauStatut}"`);
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Erreur lors du changement de statut");
                alert(`❌ ${errorData.message || "Erreur lors du changement de statut"}`);
            }
        } catch (err) {
            console.error("Erreur changement statut:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setStatusLoading(false);
        }
    }, [token, fetchStudents, pagination.page]);

    // ============================================================
    // 10. CRUD ÉTUDIANT
    // ============================================================
    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        setError(null);

        const tempPassword = Math.random().toString(36).slice(-8) +
            Math.random().toString(36).toUpperCase().slice(-2) +
            Math.random().toString(10).slice(-2);

        try {
            const payload = {
                ...formData,
                clientId: clientId,
                password: tempPassword
            };

            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/etudiants`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseData = await response.json();
                setShowFormModal(false);
                resetForm();
                await fetchStudents(pagination.page);

                const student = responseData.data || responseData;
                if (student && student.id) {
                    await handleSendNotification({
                        type: 'EMAIL',
                        subject: 'Bienvenue sur votre espace de formation',
                        message: `Bonjour ${formData.prenom} ${formData.nom},\n\nVotre compte a été créé avec succès sur la plateforme de formation.\n\n🔑 Vos identifiants de connexion :\n📧 Email : ${formData.email}\n🔒 Mot de passe : ${tempPassword}\n\n🌐 Accédez à votre espace : ${window.location.origin}/login\n\nNous vous recommandons de changer votre mot de passe lors de votre première connexion.\n\nCordialement,\nL'équipe de formation.`,
                        studentIds: [student.id]
                    });
                }

                alert("✅ Étudiant créé avec succès. Un email contenant ses identifiants lui a été envoyé.");
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Erreur lors de la création");
                alert(`❌ ${errorData.message || "Erreur lors de la création"}`);
            }
        } catch (err) {
            console.error("Erreur création:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setCreateLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/etudiants/${editingStudent.id}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setShowEditModal(false);
                setEditingStudent(null);
                resetForm();
                await fetchStudents(pagination.page);
                alert("✅ Étudiant modifié avec succès");
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Erreur lors de la modification");
                alert(`❌ ${errorData.message || "Erreur lors de la modification"}`);
            }
        } catch (err) {
            console.error("Erreur modification:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // 10b. SUPPRESSION AVEC MODE FORCE
    // ============================================================
    const handleDelete = async (force = false) => {
        if (!deletingStudent) return;

        setLoading(true);
        setError(null);

        try {
            const url = `${APP_CONFIG.BASE_URL}/api/v1/client/etudiants/${deletingStudent.id}${force ? '?force=true' : ''}`;
            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.ok) {
                setShowDeleteModal(false);
                setDeletingStudent(null);
                setForceDelete(false);
                await fetchStudents(pagination.page);
                alert("✅ Étudiant supprimé avec succès");
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Erreur lors de la suppression");
                // Si l'erreur indique des inscriptions actives, on peut laisser le modal ouvert pour cocher force
                if (errorData.message && errorData.message.includes("inscriptions actives")) {
                    alert("❌ L'étudiant a des inscriptions actives. Cochez 'Supprimer malgré...' pour les résilier automatiquement.");
                } else {
                    alert(`❌ ${errorData.message || "Erreur lors de la suppression"}`);
                }
            }
        } catch (err) {
            console.error("Erreur suppression:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // 11. NOTIFICATIONS
    // ============================================================
    const handleSendNotification = useCallback(async (notificationData) => {
        setNotificationLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/etudiants/notifications`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(notificationData)
            });

            if (response.ok) {
                setShowNotificationModal(false);
                setNotificationStudentId(null);
                return true;
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Erreur lors de l'envoi");
                alert(`❌ ${errorData.message || "Erreur lors de l'envoi"}`);
                return false;
            }
        } catch (err) {
            console.error("Erreur notification:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
            return false;
        } finally {
            setNotificationLoading(false);
        }
    }, [token]);

    const handleSendNotificationWrapper = async (notificationData) => {
        const success = await handleSendNotification(notificationData);
        if (success) {
            const count = notificationData.studentIds?.length || 0;
            alert(`✅ Notification envoyée à ${count} étudiant(s)`);
        }
    };

    // ============================================================
    // 12. IMPORT CSV
    // ============================================================
    const handleImport = async (file) => {
        if (!file) {
            alert("Veuillez sélectionner un fichier CSV");
            return;
        }

        setLoading(true);
        setError(null);

        const formDataImport = new FormData();
        formDataImport.append('file', file);
        formDataImport.append('clientId', clientId);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/etudiants/import`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`
                },
                body: formDataImport
            });

            if (response.ok) {
                const data = await response.json();
                setShowImportModal(false);
                await fetchStudents(pagination.page);
                alert(`✅ ${data.importes || 'Import'} étudiant(s) importé(s) avec succès`);
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Erreur lors de l'import");
                alert(`❌ ${errorData.message || "Erreur lors de l'import"}`);
            }
        } catch (err) {
            console.error("Erreur import:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // 13. GESTION DU FORMULAIRE
    // ============================================================
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            nom: '',
            prenom: '',
            email: '',
            telephone: '',
            dateNaissance: '',
            adresse: '',
            statut: 'ACTIF'
        });
        setError(null);
    };

    const openEditModal = (student) => {
        setEditingStudent(student);
        setFormData({
            nom: student.nom || '',
            prenom: student.prenom || '',
            email: student.email || '',
            telephone: student.telephone || '',
            dateNaissance: student.dateNaissance || '',
            adresse: student.adresse || '',
            statut: student.statut || 'ACTIF'
        });
        setShowEditModal(true);
    };

    // ============================================================
    // 14. RENDU - BADGES
    // ============================================================
    const renderStatusBadge = (statut) => {
        const statusMap = {
            'ACTIF': { label: 'Actif', class: 'bg-green-100 text-green-700 border-green-200', icon: ShieldCheck },
            'SUSPENDU': { label: 'Suspendu', class: 'bg-amber-100 text-amber-700 border-amber-200', icon: ShieldAlert },
            'INACTIF': { label: 'Inactif', class: 'bg-red-100 text-red-700 border-red-200', icon: ShieldOff }
        };

        const s = statusMap[statut] || { label: statut || 'N/A', class: 'bg-gray-100 text-gray-700 border-gray-200', icon: Shield };
        const Icon = s.icon;

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 border ${s.class}`}>
                <Icon size={12} />
                {s.label}
            </span>
        );
    };

    const renderActivationBadge = (actif) => {
        return actif ? (
            <span className="px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 border border-green-200 bg-green-100 text-green-700">
                <UserCheck size={12} />
                Actif
            </span>
        ) : (
            <span className="px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 border border-red-200 bg-red-100 text-red-700">
                <UserX size={12} />
                Inactif
            </span>
        );
    };

    // ============================================================
    // 15. AUTH
    // ============================================================
    if (!isAuthenticated || !token) {
        return (
            <div className="p-6 text-center">
                <div className="max-w-md mx-auto bg-amber-50 border border-amber-200 rounded-xl p-6">
                    <AlertCircle size={24} className="mx-auto text-amber-500 mb-2" />
                    <p className="text-gray-700 font-medium">Accès réservé aux clients</p>
                    <p className="text-sm text-gray-500 mt-1">Veuillez vous connecter pour accéder à cette page.</p>
                </div>
            </div>
        );
    }

    if (!clientId) {
        return (
            <div className="p-6 text-center">
                <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-xl p-6">
                    <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
                    <p className="text-gray-700 font-medium">Erreur de configuration</p>
                    <p className="text-sm text-gray-500 mt-1">Impossible de récupérer l'identifiant du client.</p>
                </div>
            </div>
        );
    }

    // ============================================================
    // 16. RENDU PRINCIPAL
    // ============================================================
    return (
        <div className="space-y-4 p-4">
            {/* En-tête */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-200 pb-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                        Gestion des Étudiants
                    </h2>
                    <p className="text-sm text-gray-500">
                        Gérez vos étudiants, leurs statuts et leurs comptes
                    </p>
                    <div className="text-xs text-gray-400 mt-1">
                        {students.length} étudiants
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                            style={{ maxWidth: '200px' }}
                        />
                        <button
                            onClick={() => fetchStudents(0)}
                            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
                        >
                            <Search size={16} />
                        </button>
                    </div>

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                        style={{ maxWidth: '150px' }}
                    >
                        <option value="ALL">Tous</option>
                        <option value="ACTIF">Actif</option>
                        <option value="SUSPENDU">Suspendu</option>
                        <option value="INACTIF">Inactif</option>
                    </select>

                    <button
                        onClick={() => setShowImportModal(true)}
                        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm flex items-center gap-1"
                    >
                        <Upload size={16} /> Importer
                    </button>

                    <button
                        onClick={() => {
                            setNotificationStudentId(null);
                            setShowNotificationModal(true);
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm flex items-center gap-1 disabled:opacity-50"
                        disabled={students.length === 0}
                    >
                        <Bell size={16} /> Notifier tous
                    </button>

                    <button
                        onClick={() => { resetForm(); setShowFormModal(true); }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm flex items-center gap-1"
                    >
                        <Plus size={16} /> Ajouter
                    </button>
                </div>
            </div>

            {/* Messages */}
            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}

            {/* Tableau des étudiants */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom / Prénom</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email / Téléphone</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Formation</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Compte</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading && students.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center">
                                        <Loader2 className="animate-spin mx-auto text-green-600" size={24} />
                                        <p className="text-xs text-gray-500 mt-2">Chargement...</p>
                                    </td>
                                </tr>
                            ) : students.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center">
                                        <Users size={40} className="mx-auto text-gray-300" />
                                        <p className="text-sm text-gray-400 mt-2">Aucun étudiant</p>
                                        <p className="text-xs text-gray-400">Cliquez sur "Ajouter" ou "Importer"</p>
                                    </td>
                                </tr>
                            ) : (
                                students.map(student => {
                                    const inscription = student.inscription || {};
                                    const hasInscription = !!inscription.id;

                                    return (
                                        <tr key={student.id} className={!student.actif ? 'bg-gray-50 opacity-70' : ''}>
                                            <td className="px-4 py-3">
                                                <div className="font-semibold text-gray-900">
                                                    {student.prenom} {student.nom}
                                                </div>
                                                <div className="text-[10px] text-gray-400">
                                                    ID: {student.id}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm text-gray-700">{student.email}</div>
                                                <div className="text-[11px] text-gray-500">{student.mobile || '-'}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {hasInscription ? (
                                                    <div className="font-mono text-sm font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg inline-block border border-green-200">
                                                        <Hash size={12} className="inline mr-1 text-green-400" />
                                                        {inscription.codeEtudiant || 'N/A'}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400">Non inscrit</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {hasInscription ? (
                                                    <div className="text-sm text-gray-700">
                                                        {inscription.formationTitre || '-'}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400">Aucune formation</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {hasInscription ? (
                                                    renderStatusBadge(inscription.statutEtudiant)
                                                ) : (
                                                    <span className="text-xs text-gray-400">Non inscrit</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {renderActivationBadge(student.actif !== undefined ? student.actif : true)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-center gap-1 flex-wrap">
                                                    <button
                                                        onClick={() => openEditModal(student)}
                                                        className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                                        title="Modifier"
                                                    >
                                                        <Edit size={16} />
                                                    </button>

                                                    <button
                                                        onClick={() => {
                                                            setSelectedStudent(student);
                                                            setShowResetPasswordModal(true);
                                                        }}
                                                        className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                                        title="Réinitialiser le mot de passe"
                                                    >
                                                        <Key size={16} />
                                                    </button>

                                                    <button
                                                        onClick={() => {
                                                            setNotificationStudentId(student.id);
                                                            setShowNotificationModal(true);
                                                        }}
                                                        className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                                        title="Envoyer une notification"
                                                    >
                                                        <Send size={16} />
                                                    </button>

                                                    <button
                                                        onClick={() => handleToggleActivation(student)}
                                                        className={`p-1.5 rounded-lg transition ${student.actif !== false ? 'text-gray-500 hover:text-amber-600 hover:bg-amber-50' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'}`}
                                                        title={student.actif !== false ? 'Désactiver le compte' : 'Activer le compte'}
                                                    >
                                                        {student.actif !== false ? (
                                                            <PowerOff size={16} />
                                                        ) : (
                                                            <Power size={16} />
                                                        )}
                                                    </button>

                                                    {hasInscription && (
                                                        <button
                                                            onClick={async () => {
                                                                const inscriptions = await fetchStudentInscriptions(student.id);
                                                                if (inscriptions.length > 0) {
                                                                    setSelectedStudent(student);
                                                                    setSelectedInscription(inscriptions[0]);
                                                                    setShowStatusModal(true);
                                                                } else {
                                                                    alert("❌ Aucune inscription trouvée pour cet étudiant");
                                                                }
                                                            }}
                                                            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                                            title="Changer le statut (Actif/Inactif/Suspendu)"
                                                        >
                                                            <Shield size={16} />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            setDeletingStudent(student);
                                                            setShowDeleteModal(true);
                                                            setForceDelete(false); // réinitialiser
                                                        }}
                                                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200">
                        <span className="text-xs text-gray-500">
                            {pagination.totalElements} étudiant(s) - Page {pagination.page + 1}/{pagination.totalPages}
                        </span>
                        <div className="flex gap-1">
                            <button
                                onClick={() => fetchStudents(pagination.page - 1)}
                                disabled={pagination.page === 0}
                                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 text-sm"
                            >
                                ←
                            </button>
                            <button
                                onClick={() => fetchStudents(pagination.page + 1)}
                                disabled={pagination.page + 1 >= pagination.totalPages}
                                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 text-sm"
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ============================================================
                MODAL : FORMULAIRE CRÉATION
            ============================================================ */}
            {showFormModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold flex items-center gap-2">
                                <UserPlus size={20} className="text-green-600" />
                                Nouvel étudiant
                            </h3>
                            <button onClick={() => { setShowFormModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                        </div>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm text-blue-700">
                                📧 Un email avec les identifiants de connexion sera envoyé automatiquement à l'étudiant.
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                    <input
                                        type="text"
                                        name="nom"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.nom}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                                    <input
                                        type="text"
                                        name="prenom"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.prenom}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                                    <input
                                        type="tel"
                                        name="telephone"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.telephone}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                                    <input
                                        type="date"
                                        name="dateNaissance"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.dateNaissance}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                    <select
                                        name="statut"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.statut}
                                        onChange={handleInputChange}
                                    >
                                        <option value="ACTIF">Actif</option>
                                        <option value="SUSPENDU">Suspendu</option>
                                        <option value="INACTIF">Inactif</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                                <input
                                    type="text"
                                    name="adresse"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    value={formData.adresse}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button type="button" onClick={() => { setShowFormModal(false); resetForm(); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">
                                    Annuler
                                </button>
                                <button type="submit" disabled={createLoading} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">
                                    {createLoading ? 'Création...' : 'Créer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL : ÉDITION */}
            {showEditModal && editingStudent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold flex items-center gap-2">
                                <Edit size={20} className="text-green-600" />
                                Modifier l'étudiant
                            </h3>
                            <button onClick={() => { setShowEditModal(false); setEditingStudent(null); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                        </div>
                        <form onSubmit={handleUpdate} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                    <input
                                        type="text"
                                        name="nom"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.nom}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                                    <input
                                        type="text"
                                        name="prenom"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.prenom}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                                    <input
                                        type="tel"
                                        name="telephone"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.telephone}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                                    <input
                                        type="date"
                                        name="dateNaissance"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.dateNaissance}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                    <select
                                        name="statut"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        value={formData.statut}
                                        onChange={handleInputChange}
                                    >
                                        <option value="ACTIF">Actif</option>
                                        <option value="SUSPENDU">Suspendu</option>
                                        <option value="INACTIF">Inactif</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                                <input
                                    type="text"
                                    name="adresse"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    value={formData.adresse}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button type="button" onClick={() => { setShowEditModal(false); setEditingStudent(null); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">
                                    Annuler
                                </button>
                                <button type="submit" disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">
                                    {loading ? 'Mise à jour...' : 'Mettre à jour'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL : RÉINITIALISATION DU MOT DE PASSE */}
            <ResetPasswordModal
                isOpen={showResetPasswordModal}
                onClose={() => { setShowResetPasswordModal(false); setSelectedStudent(null); }}
                onReset={handleResetPassword}
                student={selectedStudent}
                loading={resetPasswordLoading}
            />

            {/* MODAL : CHANGEMENT DE STATUT */}
            {showStatusModal && selectedStudent && selectedInscription && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold flex items-center gap-2">
                                <Shield size={20} className="text-green-600" />
                                Changer le statut de l'étudiant
                            </h3>
                            <button
                                onClick={() => {
                                    setShowStatusModal(false);
                                    setSelectedStudent(null);
                                    setSelectedInscription(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div className="bg-gray-50 p-3 rounded-lg text-sm">
                                <p className="text-gray-600">
                                    <strong>Étudiant:</strong> {selectedStudent.prenom} {selectedStudent.nom}
                                </p>
                                <p className="text-gray-600">
                                    <strong>Code:</strong> <span className="font-mono font-bold text-green-600">{selectedInscription.codeEtudiant || 'N/A'}</span>
                                </p>
                                <p className="text-gray-600">
                                    <strong>Formation:</strong> {selectedInscription.formationTitre || 'N/A'}
                                </p>
                                <p className="text-gray-600">
                                    <strong>Statut actuel:</strong>
                                    <span className="ml-2">{renderStatusBadge(selectedInscription.statutEtudiant)}</span>
                                </p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    onClick={() => handleChangeStatut(selectedInscription.id, 'ACTIF')}
                                    className={`p-3 border-2 rounded-lg transition text-center ${selectedInscription.statutEtudiant === 'ACTIF'
                                        ? 'border-green-500 bg-green-50 cursor-not-allowed opacity-60'
                                        : 'border-green-200 hover:border-green-500 hover:bg-green-50'
                                    }`}
                                    disabled={selectedInscription.statutEtudiant === 'ACTIF'}
                                >
                                    <ShieldCheck size={24} className="mx-auto text-green-600 mb-1" />
                                    <span className="text-xs font-semibold text-green-700">ACTIF</span>
                                </button>
                                <button
                                    onClick={() => handleChangeStatut(selectedInscription.id, 'SUSPENDU')}
                                    className={`p-3 border-2 rounded-lg transition text-center ${selectedInscription.statutEtudiant === 'SUSPENDU'
                                        ? 'border-amber-500 bg-amber-50 cursor-not-allowed opacity-60'
                                        : 'border-amber-200 hover:border-amber-500 hover:bg-amber-50'
                                    }`}
                                    disabled={selectedInscription.statutEtudiant === 'SUSPENDU'}
                                >
                                    <ShieldAlert size={24} className="mx-auto text-amber-600 mb-1" />
                                    <span className="text-xs font-semibold text-amber-700">SUSPENDU</span>
                                </button>
                                <button
                                    onClick={() => handleChangeStatut(selectedInscription.id, 'INACTIF')}
                                    className={`p-3 border-2 rounded-lg transition text-center ${selectedInscription.statutEtudiant === 'INACTIF'
                                        ? 'border-red-500 bg-red-50 cursor-not-allowed opacity-60'
                                        : 'border-red-200 hover:border-red-500 hover:bg-red-50'
                                    }`}
                                    disabled={selectedInscription.statutEtudiant === 'INACTIF'}
                                >
                                    <ShieldOff size={24} className="mx-auto text-red-600 mb-1" />
                                    <span className="text-xs font-semibold text-red-700">INACTIF</span>
                                </button>
                            </div>

                            {statusLoading && (
                                <div className="flex justify-center py-2">
                                    <Loader2 className="animate-spin text-green-600" size={24} />
                                    <span className="ml-2 text-sm text-gray-500">Mise à jour...</span>
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowStatusModal(false);
                                        setSelectedStudent(null);
                                        setSelectedInscription(null);
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
                                    disabled={statusLoading}
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL : CONFIRMATION SUPPRESSION AVEC OPTION FORCE */}
            {showDeleteModal && deletingStudent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-red-600">⚠️ Confirmation</h3>
                            <button onClick={() => { setShowDeleteModal(false); setDeletingStudent(null); setForceDelete(false); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                        </div>
                        <p className="text-sm text-gray-600">
                            Supprimer <strong>{deletingStudent.prenom} {deletingStudent.nom}</strong> ?
                        </p>
                        <p className="text-xs text-gray-400 mt-1">Cette action est irréversible.</p>

                        {/* Option force delete */}
                        <div className="mt-4 flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="forceDelete"
                                checked={forceDelete}
                                onChange={(e) => setForceDelete(e.target.checked)}
                                className="w-4 h-4 text-red-600 rounded"
                            />
                            <label htmlFor="forceDelete" className="text-sm text-gray-700">
                                Supprimer malgré les inscriptions actives (les résilier automatiquement)
                            </label>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <button onClick={() => { setShowDeleteModal(false); setDeletingStudent(null); setForceDelete(false); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">
                                Annuler
                            </button>
                            <button onClick={() => handleDelete(forceDelete)} disabled={loading} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 text-sm">
                                {loading ? 'Suppression...' : 'Supprimer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL : NOTIFICATIONS */}
            <NotificationModal
                isOpen={showNotificationModal}
                onClose={() => {
                    setShowNotificationModal(false);
                    setNotificationStudentId(null);
                }}
                onSend={handleSendNotificationWrapper}
                students={students}
                loading={notificationLoading}
                defaultStudentId={notificationStudentId}
            />

            {/* MODAL : IMPORT */}
            <ImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={handleImport}
                loading={loading}
            />
        </div>
    );
}